import { useState } from "react";
import { useFormik, FormikProps } from "formik";
import { SignUpFormProps } from "../types/sign-up-form-types";
import * as Yup from "yup";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { registerUser } from "services/authService";
import { updateInitialState } from "slice/merchantSlice";
import { storeUserData } from "../../../utils/storage";
import { updateHostState } from "slice/hostSlice";
import { actions } from "../../../components/layout/appLayoutSlice";
import { initializeSession } from "sessionManager";

const useSignUp = () => {
  const [showOtpDialog, setShowOtpDialog] = useState<boolean>(false);
  const isMobileVerified = useSelector(
    (state: RootState) => state.auth.isMobileVerified
  );
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const closeOtpDialog = () => {
    setShowOtpDialog(false);
  };

  const openOtpDialog = () => {
    setShowOtpDialog(true);
  };

  // Formik
  const formik: FormikProps<SignUpFormProps> = useFormik<SignUpFormProps>({
    initialValues: {
      userType: "merchant",
      name: "",
      emailAddress: "",
      mobileNumber: "",
      isOtpVerified: false,
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object().shape({
      // name: Yup.string().required("Name is required"),
      emailAddress: Yup.string().required("Email is required").matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format"),
      mobileNumber: Yup.number()
        .required("Mobile nummber is required")
        .typeError("Invalid number")
        .test(
          "length",
          "Mobile number must be 10 digits",
          (val) => val.toString().length === 10
        ),
      password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(
          /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]+$/,
          "Password must contain at least one uppercase letter, one number, and one special character"
        ),
      confirmPassword: Yup.string()
        .required("Confirm Password is required")
        .oneOf([Yup.ref("password")], "Passwords do not match"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const requestBody = {
        countryCode: "+91",
        mobileNumber: values.mobileNumber,
        emailId: values.emailAddress.toLowerCase(),
        password: values.password,
        isMobileVerified: isMobileVerified,
      };

      try {
        const response = await registerUser(values.userType === "merchant" ? "Merchant" : "Host", requestBody);
        const userId = response?.data?.user?.userId;
        storeUserData({userId, userType: values.userType});
        initializeSession()

        if (response.status === 200 || response.status === 201) {
          toast.success("Authentication successful!");
          // Navigate based on user type
          if (values.userType === "merchant") {
            dispatch(updateInitialState({...response?.data?.user}));     
          } else {
            dispatch(updateHostState({...response?.data?.user }));
          }
          dispatch(actions.setUserDetails(response?.data?.user))
          navigate(values.userType === "merchant" ? "/merchant-basic-details" : "/basic-details");
        } else if (response.status === 401) {
          toast.error("Mobile number already exists. Please login.");
          navigate("/signin");
        } else {
          toast.error("Unexpected response status.");
        }
      } catch (error: any) {
        if (error.response) {
          const status = error.response.status;
          console.error("API Error:", error.response.data);

          if (status === 401) {
            toast.error("Mobile number already exists. Please login.");
            navigate("/signin");
          } else {
            toast.error(
              `Error: ${error.response.data.message || "Something went wrong."}`
            );
          }
        } else {
          console.error("Unexpected error:", error);
          toast.error("Network or unexpected error occurred.");
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return { formik, showOtpDialog, closeOtpDialog, openOtpDialog };
};

export default useSignUp;
